/* @jsx h */
import { defineComponent, h, ref, computed, watch } from "vue";
import {
  Cart,
  CartItem,
  CartPaymentSelection,
  StaffCheckoutIntent,
  StaffCheckoutStagedPayment,
} from "@lemmonhq/types";
import { formatPrice, calculateCartItemTotal, round } from "@lemmonhq/orders";
import { useLocalization } from "@lemmonhq/uikit";
import { useAuth } from "@app/vue/providers/Auth";

type Tab = "items" | "equally" | "amount";

export type CartPaymentEditorProps = {
  cart: Cart;
  /** Already-collected staged legs (for remaining + covered-quantity math). */
  staffCheckoutPayments: StaffCheckoutStagedPayment[];
  /** Locked once the first leg is staged; disables the other mode. */
  lockedMode?: "split" | "partial";
  /** Last-persisted UI selection to restore on (re)open. */
  selection?: CartPaymentSelection;
  /** Persist the UI selection so it survives closing/reopening the sheet. */
  onSelectionChange?: (selection: CartPaymentSelection) => void;
  /**
   * When true (the tablet dock), the chosen leg is armed reactively as the
   * cashier selects/enters — no confirm button; the main Pay buttons collect
   * it. `undefined` clears the armed leg. When false (mobile sheet), a Collect
   * button confirms.
   */
  autoApply?: boolean;
  onApply: (intent: StaffCheckoutIntent | undefined) => void;
};

/** Per-unit price of a cart line, including its bundle options. */
const unitPrice = (item: CartItem) =>
  item.quantity > 0 ? calculateCartItemTotal(item) / item.quantity : 0;

export const CartPaymentEditor = defineComponent<CartPaymentEditorProps>({
  name: "CartPaymentEditor",
  props: [
    "cart",
    "staffCheckoutPayments",
    "lockedMode",
    "selection",
    "onSelectionChange",
    "autoApply",
    "onApply",
  ],
  setup(props) {
    const auth = useAuth();
    const { t } = useLocalization();
    const currency = () => auth.user.value?.restaurant.currency || "CHF";
    const fmt = (n: number) => formatPrice(n, true, 2, currency());

    // Restore the persisted selection (if any), clamped to what's still valid
    // for the current mode-lock and remaining item quantities.
    const initialTab = (): Tab => {
      const saved = props.selection?.tab;
      if (props.lockedMode === "split")
        return saved && saved !== "items" ? saved : "equally";
      if (props.lockedMode === "partial") return "items";
      return saved ?? "items";
    };
    const initialSelected = (): Record<string, number> => {
      const saved = props.selection?.selected ?? {};
      const covered: Record<string, number> = {};
      for (const p of props.staffCheckoutPayments)
        for (const it of p.items || [])
          covered[it.cartId] = (covered[it.cartId] || 0) + it.quantity;
      const out: Record<string, number> = {};
      for (const item of props.cart.items || []) {
        const remaining = item.quantity - (covered[item.cartId] || 0);
        const q = Math.min(saved[item.cartId] || 0, Math.max(0, remaining));
        if (q > 0) out[item.cartId] = q;
      }
      return out;
    };

    const tab = ref<Tab>(initialTab());

    // Partial (Split items) selection: cartId -> quantity.
    const selected = ref<Record<string, number>>(initialSelected());

    // Split equally / amount inputs.
    const splitCount = ref(Math.max(2, props.selection?.splitCount ?? 2));
    const customAmount = ref(props.selection?.customAmount ?? 0);

    // Collapsible list of already-collected legs, shown from the Paying panel.
    const showCollectedList = ref(false);

    const cartTotal = computed(() =>
      (props.cart.items || []).reduce((s, i) => s + calculateCartItemTotal(i), 0),
    );
    // Count GROSS coverage (each leg's subtotal, not the net cash), so a
    // discounted leg still covers its full share and the remaining reaches zero.
    const collected = computed(() =>
      props.staffCheckoutPayments.reduce((s, p) => s + p.subtotal, 0),
    );
    const remaining = computed(() => round(cartTotal.value - collected.value));

    // Quantity already covered per cart line across prior partial legs.
    const coveredByCartId = computed(() => {
      const map: Record<string, number> = {};
      for (const p of props.staffCheckoutPayments) {
        for (const it of p.items || []) {
          map[it.cartId] = (map[it.cartId] || 0) + it.quantity;
        }
      }
      return map;
    });
    const remainingQty = (item: CartItem) =>
      item.quantity - (coveredByCartId.value[item.cartId] || 0);
    const selectableItems = computed(() =>
      (props.cart.items || []).filter((i) => remainingQty(i) > 0),
    );

    const partialAmount = computed(() =>
      round(
        Object.entries(selected.value).reduce((sum, [cartId, qty]) => {
          const item = (props.cart.items || []).find((i) => i.cartId === cartId);
          return item ? sum + unitPrice(item) * qty : sum;
        }, 0),
      ),
    );
    // Split equally = each person pays an equal share of the WHOLE bill
    // (total / people). The FINAL leg absorbs the rounding remainder so N legs
    // sum to the whole bill (CHF 100 / 3 → 33.33, 33.33, 33.34) instead of
    // stalling at 99.99 with an uncollectable CHF 0.01. Earlier legs are capped
    // at what's left so the share can never exceed the remaining balance.
    const equalAmount = computed(() => {
      if (splitCount.value < 2) return 0;
      const remainingLegs =
        splitCount.value - props.staffCheckoutPayments.length;
      if (remainingLegs <= 1) return remaining.value;
      return Math.min(round(cartTotal.value / splitCount.value), remaining.value);
    });

    const nextAmount = computed(() => {
      if (tab.value === "items") return partialAmount.value;
      if (tab.value === "equally") return equalAmount.value;
      return round(customAmount.value);
    });

    const pct = (n: number) =>
      cartTotal.value > 0
        ? Math.max(0, Math.min(100, (n / cartTotal.value) * 100))
        : 0;
    // Progress bar is two segments: already-paid (solid) + the leg being armed
    // now (lighter), capped so the two never exceed 100% together.
    const collectedPct = computed(() => pct(collected.value));
    const nextPct = computed(() =>
      Math.min(pct(nextAmount.value), 100 - collectedPct.value),
    );

    const canApply = computed(() => {
      const amt = nextAmount.value;
      if (!(amt > 0) || round(amt) > remaining.value) return false;
      if (tab.value === "items") return Object.keys(selected.value).length > 0;
      return true;
    });

    // --- Split items interactions -------------------------------------------
    const setQty = (item: CartItem, qty: number) => {
      const clamped = Math.max(0, Math.min(qty, remainingQty(item)));
      const next = { ...selected.value };
      if (clamped === 0) delete next[item.cartId];
      else next[item.cartId] = clamped;
      selected.value = next;
    };
    const toggleItem = (item: CartItem) => {
      if ((selected.value[item.cartId] || 0) > 0) setQty(item, 0);
      else setQty(item, remainingQty(item));
    };
    const allSelected = computed(
      () =>
        selectableItems.value.length > 0 &&
        selectableItems.value.every(
          (i) => (selected.value[i.cartId] || 0) === remainingQty(i),
        ),
    );
    const toggleSelectAll = () => {
      if (allSelected.value) {
        selected.value = {};
      } else {
        const next: Record<string, number> = {};
        for (const i of selectableItems.value) next[i.cartId] = remainingQty(i);
        selected.value = next;
      }
    };

    const setTab = (next: Tab) => {
      if (next === "items" && props.lockedMode === "split") return;
      if (next !== "items" && props.lockedMode === "partial") return;
      tab.value = next;
    };

    const buildIntent = (): StaffCheckoutIntent | undefined => {
      if (!canApply.value) return undefined;
      if (tab.value === "items") {
        const items = Object.entries(selected.value).map(([cartId, qty]) => ({
          cartId,
          quantity: qty,
        }));
        return { mode: "partial", amount: partialAmount.value, items };
      }
      return { mode: "split", amount: nextAmount.value };
    };

    const handleApply = () => {
      const intent = buildIntent();
      if (intent) props.onApply(intent);
    };

    // Dock mode: arm the leg reactively as the selection/amount changes (and
    // clear it when nothing valid is chosen), so no confirm button is needed —
    // the main Pay buttons collect the armed amount.
    watch(
      [nextAmount, () => JSON.stringify(selected.value), tab],
      () => {
        if (props.autoApply) props.onApply(buildIntent());
      },
      { flush: "post" },
    );

    // Persist the UI selection so reopening the sheet restores what was chosen.
    watch(
      [tab, splitCount, customAmount, () => JSON.stringify(selected.value)],
      () => {
        props.onSelectionChange?.({
          tab: tab.value,
          splitCount: splitCount.value,
          customAmount: customAmount.value,
          selected: selected.value,
        });
      },
    );

    // Custom checkbox indicator — a native <input checked> in Vue JSX does not
    // reliably reflect state driven by the stepper, so render it ourselves.
    const CheckMark = (checked: boolean) => (
      <span
        class={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors ${
          checked
            ? "border-primary bg-primary text-primary-content"
            : "border-base-content/30"
        }`}
      >
        {checked && (
          <span class="material-symbols-outlined text-sm leading-none">
            check
          </span>
        )}
      </span>
    );

    const TabButton = (key: Tab, label: string, disabled: boolean) => (
      <button
        class={`flex-1 rounded-lg px-2 py-2 text-sm transition-all ${
          tab.value === key
            ? "bg-primary font-semibold text-primary-content shadow-sm"
            : "font-medium text-base-content/60 hover:bg-base-100/60 hover:text-base-content"
        } ${disabled ? "cursor-not-allowed opacity-30" : ""}`}
        disabled={disabled}
        onClick={() => setTab(key)}
      >
        {label}
      </button>
    );

    return () => (
      <div class="flex flex-1 min-h-0 flex-col gap-4">
        {/* Segmented tabs */}
        <div class="flex gap-1 rounded-xl bg-base-200/70 p-1">
          {TabButton(
            "items",
            t("checkout.editor.split_items"),
            props.lockedMode === "split",
          )}
          {TabButton(
            "equally",
            t("checkout.editor.split_equally"),
            props.lockedMode === "partial",
          )}
          {TabButton(
            "amount",
            t("checkout.editor.split_amount"),
            props.lockedMode === "partial",
          )}
        </div>

        {/* Content */}
        <div class="flex-1 min-h-0 space-y-2 overflow-y-auto">
          {tab.value === "items" && (
            <>
              {/* Select all — only when something is still collectable */}
              {selectableItems.value.length > 0 && (
                <button
                  class="flex w-full items-center justify-between rounded-xl bg-base-200/50 px-3 py-3 text-left"
                  onClick={toggleSelectAll}
                >
                  <div class="flex items-center gap-3">
                    {CheckMark(allSelected.value)}
                    <span class="font-medium text-base-content">
                      {t("checkout.editor.select_all")}
                    </span>
                  </div>
                  <span class="font-semibold text-accent">
                    {fmt(remaining.value)}
                  </span>
                </button>
              )}

              {(props.cart.items || []).map((item) => {
                const max = remainingQty(item);
                // Fully covered by earlier partial legs — keep it in the list
                // but cross it out (mirrors the table PaymentItem "paid" row).
                if (max <= 0) {
                  return (
                    <div
                      key={item.cartId}
                      class="flex items-center justify-between rounded-xl border border-base-content/10 bg-base-100/40 px-3 py-3 opacity-60"
                    >
                      <div class="flex min-w-0 flex-1 items-center gap-3">
                        <span class="material-symbols-outlined text-xl text-success">
                          check_circle
                        </span>
                        <span class="min-w-0 flex-1 truncate font-medium text-base-content line-through">
                          {item.name}
                        </span>
                      </div>
                      <div class="flex shrink-0 items-center gap-2">
                        <span class="text-xs font-bold uppercase text-success">
                          {t("tables.checkout.paid")}
                        </span>
                        <span class="min-w-[3.25rem] rounded bg-base-300 px-2 py-0.5 text-center font-mono text-sm text-base-content/60">
                          {item.quantity}
                        </span>
                      </div>
                    </div>
                  );
                }
                const qty = selected.value[item.cartId] || 0;
                const active = qty > 0;
                return (
                  <div
                      key={item.cartId}
                      class={`flex items-center justify-between rounded-xl border px-3 py-3 transition-colors ${
                        active
                          ? "border-primary/50 bg-primary/10"
                          : "border-base-content/10 bg-base-100"
                      }`}
                    >
                      <button
                        class="flex min-w-0 flex-1 items-center gap-3 text-left"
                        onClick={() => toggleItem(item)}
                      >
                        {CheckMark(active)}
                        <span class="min-w-0 flex-1 truncate font-medium text-base-content">
                          {item.name}
                        </span>
                      </button>
                      <div class="flex shrink-0 items-center gap-2 text-accent">
                        {!active && (
                          <span class="font-semibold">
                            {fmt(unitPrice(item) * max)}
                          </span>
                        )}
                        {active && max > 1 && (
                          <button
                            class="btn btn-xs btn-circle btn-neutral"
                            onClick={() => setQty(item, qty - 1)}
                          >
                            <span class="material-symbols-outlined text-sm">
                              remove
                            </span>
                          </button>
                        )}
                        <span class="min-w-[3.25rem] rounded bg-base-300 px-2 py-0.5 text-center font-mono text-sm font-semibold text-base-content/80">
                          {active ? `${qty}/${max}` : max}
                        </span>
                        {active && max > 1 && (
                          <button
                            class="btn btn-xs btn-circle btn-neutral"
                            disabled={qty >= max}
                            onClick={() => setQty(item, qty + 1)}
                          >
                            <span class="material-symbols-outlined text-sm">
                              add
                            </span>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
          )}

          {tab.value === "equally" && (
            <div class="flex flex-col items-center gap-6 py-8">
              <div class="flex items-center gap-5">
                <button
                  class="btn btn-circle btn-neutral"
                  disabled={splitCount.value <= 2}
                  onClick={() => (splitCount.value = splitCount.value - 1)}
                >
                  <span class="material-symbols-outlined">remove</span>
                </button>
                <div class="w-24 text-center">
                  <div class="text-3xl font-bold text-accent">
                    {splitCount.value}
                  </div>
                  <div class="text-xs text-base-content/60">
                    {t("checkout.editor.people")}
                  </div>
                </div>
                <button
                  class="btn btn-circle btn-neutral"
                  disabled={remaining.value <= 0}
                  onClick={() => (splitCount.value = splitCount.value + 1)}
                >
                  <span class="material-symbols-outlined">add</span>
                </button>
              </div>
              <div class="text-center">
                <div class="text-2xl font-bold text-accent">
                  {fmt(equalAmount.value)}
                </div>
                <div class="text-xs text-base-content/60">
                  {t("checkout.editor.per_person")}
                </div>
              </div>
            </div>
          )}

          {tab.value === "amount" && (
            <div class="flex flex-col items-center gap-3 py-8">
              <label class="text-sm text-base-content/70">
                {t("checkout.editor.enter_amount")}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max={remaining.value}
                value={customAmount.value || ""}
                placeholder="0.00"
                class="input input-bordered w-40 text-center text-lg"
                onInput={(e: Event) => {
                  const n = Number((e.target as HTMLInputElement).value);
                  customAmount.value =
                    Number.isFinite(n) && n > 0 ? Math.min(n, remaining.value) : 0;
                }}
              />
              <div class="text-xs text-base-content/50">
                {fmt(remaining.value)} {t("tables.checkout.remaining")}
              </div>
            </div>
          )}
        </div>

        {/* Paying progress + collect. Kept to a single info row + bar: at a
            till this panel competes with the keypad for vertical space. */}
        <div class="space-y-2 rounded-2xl border border-base-content/10 bg-base-200/40 px-3 py-2.5">
          <div class="flex items-center justify-between gap-2 text-sm">
            <div class="flex min-w-0 items-center gap-2">
              {collected.value > 0 && (
                <>
                  <button
                    class="flex shrink-0 items-center gap-1 text-base-content/60 hover:text-base-content"
                    onClick={() =>
                      (showCollectedList.value = !showCollectedList.value)
                    }
                  >
                    <span>{t("tables.checkout.paid")}</span>
                    <span class="rounded-full bg-base-300 px-1.5 text-xs font-semibold text-base-content/70">
                      {props.staffCheckoutPayments.length}
                    </span>
                    <span class="font-medium text-base-content/70">
                      {fmt(collected.value)}
                    </span>
                    <span class="material-symbols-outlined text-base leading-none">
                      {showCollectedList.value ? "expand_less" : "expand_more"}
                    </span>
                  </button>
                  <span class="text-base-content/30">·</span>
                </>
              )}
              <span class="truncate font-semibold text-base-content">
                {t("checkout.editor.paying")}
              </span>
            </div>
            <span class="shrink-0 whitespace-nowrap">
              <span class="font-bold text-accent">{fmt(nextAmount.value)}</span>
              <span class="text-base-content/50">
                {" "}
                {t("reports.tx.open_checkouts.collected_of")}{" "}
                {fmt(cartTotal.value)}
              </span>
            </span>
          </div>
          {collected.value > 0 && showCollectedList.value && (
            <div class="rounded-xl bg-base-100/60 px-2 py-1">
              {props.staffCheckoutPayments.map((p, i) => (
                <div
                  key={p.paymentGuid || i}
                  class="flex items-center justify-between px-1 text-sm"
                >
                  <span class="flex items-center gap-2 capitalize text-base-content/70">
                    <span class="material-symbols-outlined text-base leading-none text-base-content/40">
                      {p.method === "cash" ? "payments" : "receipt_long"}
                    </span>
                    {t("reports.tx.open_checkouts.leg")} {i + 1} · {p.method}
                  </span>
                  <span class="font-medium text-base-content">
                    {fmt(p.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div class="flex h-1.5 w-full overflow-hidden rounded-full bg-base-300/70">
            <div
              class="h-full bg-success transition-all duration-300"
              style={{ width: `${collectedPct.value}%` }}
            />
            <div
              class="h-full bg-success/40 transition-all duration-300"
              style={{ width: `${nextPct.value}%` }}
            />
          </div>
          {!props.autoApply && (
            <button
              class="btn btn-primary btn-block gap-2"
              disabled={!canApply.value}
              onClick={handleApply}
            >
              <span class="material-symbols-outlined">point_of_sale</span>
              {t("checkout.editor.collect")}
            </button>
          )}
        </div>
      </div>
    );
  },
});
